import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Options for {@link dateRangeValidator}.
 */
export interface DateRangeValidatorOptions {
  /**
   * Allows the range to start in the past. Use for ranges that filter records that
   * already exist (for example an "applied date" range) instead of scheduling future work.
   * Defaults to `false`, which requires the start date/time to be current or later.
   */
  allowPastDates?: boolean;
}

/**
 * Validator for date ranges ensuring end date is after start date.
 * Works with form groups that have startDate and endDate controls.
 * Also validates that dates are not in the past and handles time validation,
 * unless `allowPastDates` is set.
 * 
 * @param options - Optional behaviour overrides, see {@link DateRangeValidatorOptions}
 * @returns ValidatorFn that validates date and time ranges
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   startDate: [''],
 *   startTime: [''],
 *   endDate: [''],
 *   endTime: ['']
 * }, { validators: dateRangeValidator() });
 * ```
 * 
 * @example
 * ```typescript
 * // Filtering on dates that have already happened
 * this.form = this.fb.group({
 *   startDate: [''],
 *   endDate: ['']
 * }, { validators: dateRangeValidator({ allowPastDates: true }) });
 * ```
 */
export function dateRangeValidator(options: DateRangeValidatorOptions = {}): ValidatorFn {
  const { allowPastDates = false } = options;

  return (control: AbstractControl): ValidationErrors | null => {
    const startDate = control.get('startDate')?.value;
    const endDate = control.get('endDate')?.value;
    const startTime = control.get('startTime')?.value;
    const endTime = control.get('endTime')?.value;

    // Check if startdate is past date
    if (!allowPastDates && startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedStart = new Date(startDate);
      selectedStart.setHours(0, 0, 0, 0);
      
      if (selectedStart < today) {
        return { startDateInPast: true };
      }
    }
    
    // Check if todays date, start time should be future time
    if (!allowPastDates && startDate && startTime) { 
      const now = new Date();
      const start = setDateTime(new Date(startDate), startTime);
      const startDateObj = new Date(startDate);
      const isToday = startDateObj.toDateString() === new Date().toDateString();
      if (isToday && start <= now) {
        return { startTimeInPast: true };
      }
    }

    // End date validation
    if (startDate && endDate) {
      const selectedStart = new Date(startDate);
      selectedStart.setHours(0, 0, 0, 0);
      const selectedEnd = new Date(endDate);
      selectedEnd.setHours(0, 0, 0, 0);
      
      if (selectedEnd < selectedStart) {
        return { endDateInPast: true };
      }
    }
    
    // End date, End time validation
    if (startDate && startTime && endDate && endTime) {
      const start = setDateTime(new Date(startDate), startTime);
      const end = setDateTime(new Date(control.get('endDate')?.value), control.get('endTime')?.value);
      if (end <= start) {
        return { endBeforeStart: true };
      }
    }

    return null;
  };
}

/**
 * Helper function to set time on a date object
 * @param date - The date to set time on
 * @param time - The time value to apply
 * @returns Date object with time set
 */
function setDateTime(date: Date, time: Date): Date {
  date.setHours(
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    0
  );
  return date;
}

/**
 * Validator that ensures a date is not in the past.
 * 
 * @returns ValidatorFn that validates date is today or in the future
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   eventDate: ['', [Validators.required, futureDateValidator()]]
 * });
 * ```
 */
export function futureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(control.value);
    selectedDate.setHours(0, 0, 0, 0);

    return selectedDate < today ? { pastDate: true } : null;
  };
}

/**
 * Options for {@link pastDateValidator}.
 */
export interface PastDateValidatorOptions {
  /**
   * Accepts today's date as well as past dates. Use for dates that record something that
   * has already happened, which can be today (for example a validation date).
   * Defaults to `false`, which requires the date to be before today.
   */
  allowToday?: boolean;
}

/**
 * Validator that ensures a date is in the past.
 *
 * @param options - Optional behaviour overrides, see {@link PastDateValidatorOptions}
 * @returns ValidatorFn that validates date is in the past
 *
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   birthDate: ['', [Validators.required, pastDateValidator()]]
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Today or earlier
 * this.form = this.fb.group({
 *   validatedDate: ['', [Validators.required, pastDateValidator({ allowToday: true })]]
 * });
 * ```
 */
export function pastDateValidator(options: PastDateValidatorOptions = {}): ValidatorFn {
  const { allowToday = false } = options;

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(control.value);
    selectedDate.setHours(0, 0, 0, 0);

    const isFuture = allowToday ? selectedDate > today : selectedDate >= today;
    return isFuture ? { futureDate: true } : null;
  };
}

/**
 * Ensures the date year is on or after minYear.
 * Prefers Moment.year() so year 0001 is not coerced to 2001 by Date parsing.
 */
export function minYearValidator(minYear: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const year = control.value.year?.() ?? new Date(control.value).getFullYear();
    return year < minYear ? { matDatepickerMin: true } : null;
  };
}